import React, { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { authStore } from "../../lib/api";
import { Button } from "../ui/button";
import { PdfViewer } from "./PdfViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * 项目文件 PDF：登录态查看器（取页数 → 逐页渲染）；解析失败回退到浏览器内嵌。
 *
 * `convert`（2026-10-20，用户需求「其它格式支持吗」）：
 *   docx / xlsx / pptx 这类浏览器无法渲染的 Office 文档，后端会先用 LibreOffice
 *   转成 PDF 再逐页出图。此时页数接口走 `/files/{fid}/pages?convert=1`，
 *   每页图片接口也带 `convert=1`，两条链路的参数必须一致 —— 否则取到的是
 *   原文件的页数、却去渲染一份还没生成的 PDF。
 *
 * 转换是**懒触发 + 落库缓存**：第一次点开要等几秒（后端跑 soffice），
 * 之后直接命中缓存，所以这里保留 loading 态并提示「首次转换较慢」。
 */
export const ProjectPdfPreview = ({ api, file, zh, extra, convert = false, downloadUrl, allowBrowserFallback = true, showThumbnails }) => {
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [retry, setRetry] = useState(0);
  const token = encodeURIComponent(authStore.get("ali_token") || "");
  const base = (api?.defaults?.baseURL || `${BACKEND || ""}/api`).replace(/\/$/, "");
  const fid = encodeURIComponent(file?.fileId || "");
  const dl = downloadUrl || `${base}/files/${fid}/download?auth=${token}`;
  const cv = convert ? "&convert=1" : "";
  const pagesUrl = `/files/${fid}/pages${convert ? "?convert=1" : ""}`;
  const pageUrl = (n, s) => `${base}/files/${fid}/page/${n}?size=${s}${cv}&auth=${token}`;
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setMeta(null); setErr(null);
    api.get(pagesUrl, { signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        if (!Number.isInteger(data?.pageCount) || data.pageCount < 1 || !Array.isArray(data.sizes) || data.sizes.length !== data.pageCount
          || data.sizes.some(size => !Array.isArray(size) || size.length !== 2 || size.some(value => !Number.isFinite(value) || value <= 0))) throw new Error(zh ? "无法读取文档页面" : "Unable to read document pages");
        setMeta(data);
      })
      .catch((e) => { if (active) setErr(e?.detailText || e.message); });
    return () => { active = false; controller.abort(); };
  }, [api, pagesUrl, retry, zh]);
  // 财务凭证必须在没有 PDF 插件的手机与内置浏览器中可读；失败可重试，不能再回到空白 iframe。
  if (err && !allowBrowserFallback) return <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-center" data-testid="pdf-fallback">
    <AlertTriangle className="h-6 w-6 text-amber-600" aria-hidden="true" />
    <p role="alert" className="text-sm text-slate-700">{zh ? "文档预览失败，可重试或下载原件。" : "Document preview failed. Retry or download the original."}</p>
    <Button type="button" variant="outline" className="min-h-11" onClick={() => setRetry(value => value + 1)} data-testid="pdf-retry"><RefreshCw className="mr-1 h-4 w-4" />{zh ? "重试" : "Retry"}</Button>
    <a href={dl} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm text-brand-600 underline focus-visible:ring-2 focus-visible:ring-brand-400">{zh ? "下载原件" : "Download original"}</a>
  </div>;
  if (err) return (
    <div className="h-full flex flex-col" data-testid="pdf-fallback">
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />{zh ? "逐页预览不可用，已切换为浏览器内嵌预览" : "Page view unavailable, showing browser preview"} · {String(err)}</p>
      <iframe title={file?.filename || "PDF"} src={dl} className="w-full flex-1 rounded-md border border-slate-200 bg-white" data-testid="file-preview-frame" />
      <a href={dl} target="_blank" rel="noreferrer" className="mt-2 self-end inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-500 hover:underline"><ExternalLink className="h-3.5 w-3.5" />{zh ? "在新标签页打开 / 下载" : "Open in new tab / download"}</a>
    </div>
  );
  if (!meta) return (
    <div className="h-full flex items-center justify-center bg-slate-900 rounded-xl text-slate-300 text-xs gap-2" data-testid="pdf-loading">
      <Loader2 className="h-4 w-4 animate-spin" />
      {convert
        ? (zh ? "正在把 Office 文档转为可预览页面（首次较慢，之后秒开）…" : "Converting Office document for preview…")
        : (zh ? "解析图纸页面…" : "Reading pages…")}
    </div>
  );
  return <PdfViewer key={pagesUrl} meta={meta} zh={zh} extra={extra} downloadUrl={dl} tp="pdf" initialThumbnails={showThumbnails} pageUrl={pageUrl} />;
};
